export type CustomFetchOptions = RequestInit & {
  responseType?: "json" | "text" | "blob" | "auto";
};

export type ErrorType<T = unknown> = ApiError<T>;

export type BodyType<T> = T;

export type AuthTokenGetter = () => Promise<string | null> | string | null;

const NO_BODY_STATUS = new Set([204, 205, 304]);
const DEFAULT_JSON_ACCEPT = "application/json, application/problem+json";

// ---------------------------------------------------------------------------
// Module-level configuration
// ---------------------------------------------------------------------------

let _baseUrl: string | null = null;
let _authTokenGetter: AuthTokenGetter | null = null;

const SESSION_STORAGE_KEY = "vnz_session_id";
const CUSTOMER_SESSION_STORAGE_KEY = "vnz_customer_session_id";

export function storeSessionId(id: string | null): void {
  try {
    if (id) {
      localStorage.setItem(SESSION_STORAGE_KEY, id);
    } else {
      localStorage.removeItem(SESSION_STORAGE_KEY);
    }
  } catch {
    // localStorage may be unavailable in some environments
  }
}

export function getStoredSessionId(): string | null {
  try {
    return localStorage.getItem(SESSION_STORAGE_KEY);
  } catch {
    return null;
  }
}

export function storeCustomerSessionId(id: string | null): void {
  try {
    if (id) {
      localStorage.setItem(CUSTOMER_SESSION_STORAGE_KEY, id);
    } else {
      localStorage.removeItem(CUSTOMER_SESSION_STORAGE_KEY);
    }
  } catch {
    // localStorage may be unavailable in some environments
  }
}

export function getStoredCustomerSessionId(): string | null {
  try {
    return localStorage.getItem(CUSTOMER_SESSION_STORAGE_KEY);
  } catch {
    return null;
  }
}

/**
 * Set a base URL that is prepended to every relative request URL
 * (i.e. paths that start with `/`).
 *
 * Useful for Expo bundles that need to call a remote API server.
 * Pass `null` to clear the base URL.
 */
export function setBaseUrl(url: string | null): void {
  _baseUrl = url ? url.replace(/\/+$/, "") : null;
}

/**
 * Register a getter that supplies a bearer auth token.  Before every fetch
 * the getter is invoked; when it returns a non-null string, an
 * `Authorization: Bearer <token>` header is attached to the request.
 *
 * Useful for Expo bundles making token-gated API calls.
 * Pass `null` to clear the getter.
 */
export function setAuthTokenGetter(getter: AuthTokenGetter | null): void {
  _authTokenGetter = getter;
}

function isRequest(input: RequestInfo | URL): input is Request {
  return typeof Request !== "undefined" && input instanceof Request;
}

function resolveMethod(input: RequestInfo | URL, explicitMethod?: string): string {
  if (explicitMethod) return explicitMethod.toUpperCase();
  if (isRequest(input)) return input.method.toUpperCase();
  return "GET";
}

// Use loose check for URL — some runtimes (e.g. React Native) polyfill URL
// differently, so `instanceof URL` can fail.
function isUrl(input: RequestInfo | URL): input is URL {
  return typeof URL !== "undefined" && input instanceof URL;
}

function applyBaseUrl(input: RequestInfo | URL): RequestInfo | URL {
  if (!_baseUrl) return input;
  const url = resolveUrl(input);
  // Only prepend to relative paths (starting with /)
  if (!url.startsWith("/")) return input;

  const absolute = `${_baseUrl}${url}`;
  if (typeof input === "string") return absolute;
  if (isUrl(input)) return new URL(absolute);
  return new Request(absolute, input as Request);
}

function resolveUrl(input: RequestInfo | URL): string {
  if (typeof input === "string") return input;
  if (isUrl(input)) return input.toString();
  return input.url;
}

function mergeHeaders(...sources: Array<HeadersInit | undefined>): Headers {
  const headers = new Headers();

  for (const source of sources) {
    if (!source) continue;
    new Headers(source).forEach((value, key) => {
      headers.set(key, value);
    });
  }

  return headers;
}

function getMediaType(headers: Headers): string | null {
  const value = headers.get("content-type");
  return value ? value.split(";", 1)[0].trim().toLowerCase() : null;
}

function isJsonMediaType(mediaType: string | null): boolean {
  return mediaType === "application/json" || Boolean(mediaType?.endsWith("+json"));
}

function isTextMediaType(mediaType: string | null): boolean {
  return Boolean(
    mediaType &&
      (mediaType.startsWith("text/") ||
        mediaType === "application/xml" ||
        mediaType === "text/xml" ||
        mediaType.endsWith("+xml") ||
        mediaType === "application/x-www-form-urlencoded"),
  );
}

// Use strict equality: in browsers, `response.body` is `null` when the
// response genuinely has no content.  In React Native, `response.body` is
// always `undefined` because the ReadableStream API is not implemented —
// even when the response carries a full payload readable via `.text()` or
// `.json()`.  Loose equality (`== null`) matches both `null` and `undefined`,
// which causes every React Native response to be treated as empty.
function hasNoBody(response: Response, method: string): boolean {
  if (method === "HEAD") return true;
  if (NO_BODY_STATUS.has(response.status)) return true;
  if (response.headers.get("content-length") === "0") return true;
  if (response.body === null) return true;
  return false;
}

function stripBom(text: string): string {
  return text.charCodeAt(0) === 0xfeff ? text.slice(1) : text;
}

function looksLikeJson(text: string): boolean {
  const trimmed = text.trimStart();
  return trimmed.startsWith("{") || trimmed.startsWith("[");
}

function getStringField(value: unknown, key: string): string | undefined {
  if (!value || typeof value !== "object") return undefined;

  const candidate = (value as Record<string, unknown>)[key];
  if (typeof candidate !== "string") return undefined;

  const trimmed = candidate.trim();
  return trimmed === "" ? undefined : trimmed;
}

function truncate(text: string, maxLength = 300): string {
  return text.length > maxLength ? `${text.slice(0, maxLength - 1)}…` : text;
}

function buildErrorMessage(response: Response, data: unknown): string {
  const prefix = `HTTP ${response.status} ${response.statusText}`;

  if (typeof data === "string") {
    const text = data.trim();
    return text ? `${prefix}: ${truncate(text)}` : prefix;
  }

  // Shape padrão do api-server é aninhado: { error: { code, message } }.
  // Sem isso, o cliente via "HTTP 400" cru em vez da mensagem PT-BR.
  const nestedError =
    data && typeof data === "object" && typeof (data as Record<string, unknown>)["error"] === "object"
      ? (data as Record<string, unknown>)["error"]
      : undefined;

  const title = getStringField(data, "title");
  const detail = getStringField(data, "detail");
  const message =
    getStringField(nestedError, "message") ??
    getStringField(data, "message") ??
    getStringField(data, "error_description") ??
    getStringField(data, "error");

  if (title && detail) return `${prefix}: ${title} — ${detail}`;
  if (detail) return `${prefix}: ${detail}`;
  if (message) return `${prefix}: ${message}`;
  if (title) return `${prefix}: ${title}`;

  return prefix;
}

// Reporter opcional injetado pela SPA (ErrorBoundary.tsx → main.tsx) pra
// capturar respostas HTTP 4xx/5xx em client_errors. Sem isso, erros HTTP
// morrem no .catch() do React Query/mutation e nao chegam aos handlers
// window.error/unhandledrejection — ficando invisiveis no painel.
let apiErrorReporter: ((err: ReportableFetchError) => void) | null = null;

export function setApiErrorReporter(
  fn: ((err: ReportableFetchError) => void) | null,
): void {
  apiErrorReporter = fn;
}

export class ApiError<T = unknown> extends Error {
  readonly name = "ApiError";
  readonly status: number;
  readonly statusText: string;
  readonly data: T | null;
  readonly headers: Headers;
  readonly response: Response;
  readonly method: string;
  readonly url: string;

  constructor(
    response: Response,
    data: T | null,
    requestInfo: { method: string; url: string },
  ) {
    super(buildErrorMessage(response, data));
    Object.setPrototypeOf(this, new.target.prototype);

    this.status = response.status;
    this.statusText = response.statusText;
    this.data = data;
    this.headers = response.headers;
    this.response = response;
    this.method = requestInfo.method;
    this.url = response.url || requestInfo.url;
  }
}

// Falha em que NÃO houve resposta HTTP: DNS, TLS, conexão recusada, timeout,
// CORS bloqueado, portal de operadora derrubando a conexão. O `fetch` rejeita
// antes de existir um `Response`, então nada disso virava `ApiError` e o
// reporter nunca era chamado — o app inteiro era cego para queda de rede.
// Descoberto em 01/08/2026 investigando "Link inválido" intermitente: o token
// da lojista era permanente e não podia dar 400, e mesmo assim não havia UM
// registro na telemetria, porque o caso dela é justamente este.
//
// `status = 0` é a convenção para "sem resposta" e é o que o reporter usa para
// separar isto de um erro HTTP de verdade.
export class NetworkError extends Error {
  readonly name = "NetworkError";
  readonly status = 0;
  readonly method: string;
  readonly url: string;

  constructor(cause: unknown, requestInfo: { method: string; url: string }) {
    const detalhe = cause instanceof Error ? cause.message : String(cause);
    super(`Sem resposta da API — ${detalhe}`);
    Object.setPrototypeOf(this, new.target.prototype);
    this.method = requestInfo.method;
    this.url = requestInfo.url;
    if (cause instanceof Error && cause.stack) this.stack = cause.stack;
  }
}

export type ReportableFetchError = ApiError | NetworkError;

export class ResponseParseError extends Error {
  readonly name = "ResponseParseError";
  readonly status: number;
  readonly statusText: string;
  readonly headers: Headers;
  readonly response: Response;
  readonly method: string;
  readonly url: string;
  readonly rawBody: string;
  readonly cause: unknown;

  constructor(
    response: Response,
    rawBody: string,
    cause: unknown,
    requestInfo: { method: string; url: string },
  ) {
    super(
      `Failed to parse response from ${requestInfo.method} ${response.url || requestInfo.url} ` +
        `(${response.status} ${response.statusText}) as JSON`,
    );
    Object.setPrototypeOf(this, new.target.prototype);

    this.status = response.status;
    this.statusText = response.statusText;
    this.headers = response.headers;
    this.response = response;
    this.method = requestInfo.method;
    this.url = response.url || requestInfo.url;
    this.rawBody = rawBody;
    this.cause = cause;
  }
}

async function parseJsonBody(
  response: Response,
  requestInfo: { method: string; url: string },
): Promise<unknown> {
  const raw = await response.text();
  const normalized = stripBom(raw);

  if (normalized.trim() === "") {
    return null;
  }

  try {
    return JSON.parse(normalized);
  } catch (cause) {
    throw new ResponseParseError(response, raw, cause, requestInfo);
  }
}

async function parseErrorBody(response: Response, method: string): Promise<unknown> {
  if (hasNoBody(response, method)) {
    return null;
  }

  const mediaType = getMediaType(response.headers);

  // Fall back to text when blob() is unavailable (e.g. some React Native builds).
  if (mediaType && !isJsonMediaType(mediaType) && !isTextMediaType(mediaType)) {
    return typeof response.blob === "function" ? response.blob() : response.text();
  }

  const raw = await response.text();
  const normalized = stripBom(raw);
  const trimmed = normalized.trim();

  if (trimmed === "") {
    return null;
  }

  if (isJsonMediaType(mediaType) || looksLikeJson(normalized)) {
    try {
      return JSON.parse(normalized);
    } catch {
      return raw;
    }
  }

  return raw;
}

function inferResponseType(response: Response): "json" | "text" | "blob" {
  const mediaType = getMediaType(response.headers);

  if (isJsonMediaType(mediaType)) return "json";
  if (isTextMediaType(mediaType) || mediaType == null) return "text";
  return "blob";
}

async function parseSuccessBody(
  response: Response,
  responseType: "json" | "text" | "blob" | "auto",
  requestInfo: { method: string; url: string },
): Promise<unknown> {
  if (hasNoBody(response, requestInfo.method)) {
    return null;
  }

  const effectiveType =
    responseType === "auto" ? inferResponseType(response) : responseType;

  switch (effectiveType) {
    case "json":
      return parseJsonBody(response, requestInfo);

    case "text": {
      const text = await response.text();
      return text === "" ? null : text;
    }

    case "blob":
      if (typeof response.blob !== "function") {
        throw new TypeError(
          "Blob responses are not supported in this runtime. " +
            "Use responseType \"json\" or \"text\" instead.",
        );
      }
      return response.blob();
  }
}

async function performFetch<T>(
  input: RequestInfo | URL,
  options: CustomFetchOptions,
  sessionTokenSource: "owner" | "customer",
): Promise<T> {
  input = applyBaseUrl(input);
  const { responseType = "auto", headers: headersInit, ...init } = options;

  const method = resolveMethod(input, init.method);

  if (init.body != null && (method === "GET" || method === "HEAD")) {
    throw new TypeError(`customFetch: ${method} requests cannot have a body.`);
  }

  const headers = mergeHeaders(isRequest(input) ? input.headers : undefined, headersInit);

  if (
    typeof init.body === "string" &&
    !headers.has("content-type") &&
    looksLikeJson(init.body)
  ) {
    headers.set("content-type", "application/json");
  }

  if (responseType === "json" && !headers.has("accept")) {
    headers.set("accept", DEFAULT_JSON_ACCEPT);
  }

  // Attach bearer token when an auth getter is configured and no
  // Authorization header has been explicitly provided.
  if (_authTokenGetter && !headers.has("authorization")) {
    const token = await _authTokenGetter();
    if (token) {
      headers.set("authorization", `Bearer ${token}`);
    }
  }

  // Owner and customer sessions are stored under separate localStorage keys
  // (vnz_session_id vs vnz_customer_session_id) and sent via x-session-token
  // for cross-origin contexts where cookies are blocked. customerCustomFetch
  // always uses the customer key; customFetch picks the customer key for
  // customer-scoped paths and the owner key otherwise.
  let sessionId: string | null;
  if (sessionTokenSource === "customer") {
    sessionId = getStoredCustomerSessionId();
  } else {
    const url = resolveUrl(input);
    const path = (() => {
      try {
        return new URL(url, "http://x").pathname;
      } catch {
        return url;
      }
    })();
    const isCustomerScopedPath =
      path.startsWith("/api/customer/") ||
      path.startsWith("/api/catalog/") ||
      path.startsWith("/api/booking/");
    if (isCustomerScopedPath) {
      sessionId = getStoredCustomerSessionId() || getStoredSessionId();
    } else {
      sessionId = getStoredSessionId() || getStoredCustomerSessionId();
    }
  }
  if (sessionId && !headers.has("x-session-token")) {
    headers.set("x-session-token", sessionId);
  }

  const requestInfo = { method, url: resolveUrl(input) };
  let response: Response;
  try {
    response = await fetch(input, { ...init, method, headers, credentials: "include" });
  } catch (cause) {
    // Cancelamento não é falha: componente desmontou, usuário navegou, React
    // Query abortou. Reportar isso encheria a telemetria de ruído a cada troca
    // de tela.
    const abortado =
      (cause instanceof DOMException && cause.name === "AbortError") ||
      (cause instanceof Error && cause.name === "AbortError");
    if (!abortado && apiErrorReporter) {
      try {
        apiErrorReporter(new NetworkError(cause, requestInfo));
      } catch {
        // never throw from reporter hook
      }
    }
    // Relança o erro ORIGINAL, não o NetworkError: quem trata `catch` hoje
    // (extractApiErrorMessage, telas, React Query) continua vendo exatamente o
    // que via antes. Esta mudança só ACRESCENTA telemetria — trocar o tipo
    // lançado mudaria comportamento em todo o app sem necessidade.
    throw cause;
  }

  if (!response.ok) {
    const errorData = await parseErrorBody(response, method);
    const apiError = new ApiError(response, errorData, requestInfo);
    if (apiErrorReporter) {
      try {
        apiErrorReporter(apiError);
      } catch {
        // never throw from reporter hook
      }
    }
    throw apiError;
  }

  // 200 com HTML NUNCA é resposta da nossa API. Ou a requisição foi
  // interceptada no meio do caminho (portal de operadora / Wi-Fi com login
  // devolve uma página com status 200 pra qualquer coisa), ou ela bateu no
  // Worker da borda, cujo fallback de SPA serve o index.html com 200.
  // Sem este guarda o HTML era aceito como corpo de sucesso: a mutação
  // resolvia, a UI dizia "Transação salva!" e fechava a tela — sem nada ter
  // sido gravado e sem 4xx/5xx pra aparecer na telemetria (Açaí ronconi,
  // 26/07/2026). Quem pede text/blob explicitamente segue livre.
  if (
    responseType !== "text" &&
    responseType !== "blob" &&
    getMediaType(response.headers) === "text/html"
  ) {
    const apiError = new ApiError(
      response,
      "Resposta inesperada (HTML) — a conexão pode estar sendo interceptada.",
      requestInfo,
    );
    if (apiErrorReporter) {
      try {
        apiErrorReporter(apiError);
      } catch {
        // never throw from reporter hook
      }
    }
    throw apiError;
  }

  return (await parseSuccessBody(response, responseType, requestInfo)) as T;
}

export async function customFetch<T = unknown>(
  input: RequestInfo | URL,
  options: CustomFetchOptions = {},
): Promise<T> {
  return performFetch<T>(input, options, "owner");
}

export async function customerCustomFetch<T = unknown>(
  input: RequestInfo | URL,
  options: CustomFetchOptions = {},
): Promise<T> {
  return performFetch<T>(input, options, "customer");
}
